/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { type IntlShape } from 'react-intl';
import { di } from 'react-magnetic-di';

import { jsx } from '@atlaskit/css'; // eslint-disable-line @typescript-eslint/no-unused-vars
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import {
	encodeAdf,
	encodeSkillPromptToAdf,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import type { Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type {
	SelectedPinnedItem,
	SelectedSpace,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { ForYouPanel } from '@atlassian/rovo-for-you/for-you-panel';
import type { ChatEmptyLaunchParams } from '@atlassian/rovo-for-you/types';

import type { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { ConversationAssistantUIModes } from '../../types';

import { RovoAgentEmptyState } from './rovo-agent-empty-state';
import { RovoEmptyState } from './rovo-empty-state';
import { FOR_YOU_TAB_ID, type RovoTabId, SUGGESTIONS_TAB_ID } from './rovo-tabs/constants';
import tabMessages from './rovo-tabs/messages';
import { RovoTabs } from './rovo-tabs/RovoTabs';

type SendMessage = ReturnType<typeof useChatStoreActions>['sendMessage'];

type EmptyStateChatMode = {
	webSearchEnabled?: boolean;
	useCurrentPageContext?: boolean;
	appFilters?: RovoApp[];
};

type EmptyStateSendMessage = (prompt: string, handleType: HandleActionType, agent?: Agent) => void;

type UseEmptyStateRenderParams = {
	activeTabId?: RovoTabId;
	conversation?: Conversation;
	conversationAgent?: Agent;
	conversationId: string;
	conversationMessagesResponseCount: number | null;
	currentChatMode: EmptyStateChatMode;
	elements?: {
		shouldShowConversationActions?: boolean;
	};
	formatMessage: IntlShape['formatMessage'];
	handleChatEmptyLaunch: (params: ChatEmptyLaunchParams) => void;
	handleClickDynamicPrompt?: (starter: ConversationAction) => void;
	isConversationAgentDefault: boolean;
	isRenderStabilizationEnabled: boolean;
	isResizeEnabled: boolean;
	isResizeHeaderThresholdReached: boolean | null;
	mode?: ConversationAssistantUIModes;
	onActiveTabChange?: (tabId: string) => void;
	onDiscoverPromptLibraryClick?: () => void;
	product?: string;
	profileData: {
		aaid?: string | null;
	};
	runtimeCustomGreeting?: unknown;
	selectedPinnedItem?: SelectedPinnedItem;
	selectedSpace?: SelectedSpace;
	sendMessage: SendMessage;
	siteId: string;
	storeUserMessages?: boolean;
};

const sendEmptyStateMessage = ({
	agent,
	conversationId,
	appFilters,
	handleType,
	prompt,
	resolvedSpaceId,
	sendMessage,
	storeUserMessages,
	useCurrentPageContext,
	webSearchEnabled,
}: {
	agent?: Agent;
	conversationId: string;
	appFilters?: RovoApp[];
	handleType: HandleActionType;
	prompt: string;
	resolvedSpaceId?: string;
	sendMessage: SendMessage;
	storeUserMessages?: boolean;
	useCurrentPageContext?: boolean;
	webSearchEnabled?: boolean;
}) => {
	const spaceContext = resolvedSpaceId
		? {
				spaceId: resolvedSpaceId,
			}
		: {};

	sendMessage({
		agent: agent ?? undefined,
		prompt:
			handleType === HandleActionType.Skill
				? encodeSkillPromptToAdf(prompt).adf
				: encodeAdf(prompt),
		conversationId,
		storeUserMessages: storeUserMessages ?? false,
		messageSources: {
			// TODO: align on interaction source keys.
			followUpSource: 'aiMate',
		},
		deepResearchEnabled: undefined,
		thinkDeeperEnabled: undefined,
		fastModeEnabled: undefined,
		taskModeEnabled: undefined,
		webSearchEnabled,
		addCurrentPageAsContextUserPreference: useCurrentPageContext,
		appFilters,
		...spaceContext,
	});
};

export const useEmptyStateRender = ({
	activeTabId,
	conversation,
	conversationAgent,
	conversationId,
	conversationMessagesResponseCount,
	currentChatMode,
	elements,
	formatMessage,
	handleChatEmptyLaunch,
	handleClickDynamicPrompt,
	isConversationAgentDefault,
	isRenderStabilizationEnabled,
	isResizeEnabled,
	isResizeHeaderThresholdReached,
	mode,
	onActiveTabChange,
	onDiscoverPromptLibraryClick,
	product,
	profileData,
	runtimeCustomGreeting,
	selectedPinnedItem,
	selectedSpace,
	sendMessage,
	siteId,
	storeUserMessages,
}: UseEmptyStateRenderParams): ReactNode => {
	di(fg);

	const isFypTabsEnabled = FeatureGates.getExperimentValue<boolean>(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	);
	// Lifted from RovoEmptyState so the RovoTabs strip can hide while a nested
	// action menu is open. Reset on surface change to avoid a stuck hidden state.
	const [isInNestedActionView, setIsInNestedActionView] = useState(false);
	useEffect(() => {
		setIsInNestedActionView(false);
	}, [mode, activeTabId, conversationId]);

	const webSearchEnabled = currentChatMode.webSearchEnabled;
	const useCurrentPageContext = currentChatMode.useCurrentPageContext;
	const appFilters = currentChatMode.appFilters;
	const selectedSpaceId = selectedSpace?.id;
	const conversationRovoSpaceId = conversation?.rovoSpaceId;

	const handleStabilizedSendMessage = useCallback<EmptyStateSendMessage>(
		(prompt, handleType, agent) => {
			sendEmptyStateMessage({
				agent,
				appFilters,
				conversationId,
				handleType,
				prompt,
				resolvedSpaceId:
					(selectedSpaceId ?? conversationRovoSpaceId) &&
					FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
						? (selectedSpaceId ?? conversationRovoSpaceId)
						: undefined,
				sendMessage,
				storeUserMessages,
				useCurrentPageContext,
				webSearchEnabled,
			});
		},
		[
			appFilters,
			conversationRovoSpaceId,
			conversationId,
			selectedSpaceId,
			sendMessage,
			storeUserMessages,
			useCurrentPageContext,
			webSearchEnabled,
		],
	);

	const renderEmptyStateContent = useCallback(
		(emptyStateSendMessage: EmptyStateSendMessage) => {
			const shouldBypassTabsForEmptyLaunch =
				conversationId === EMPTY_CONVERSATION_ID && Boolean(runtimeCustomGreeting);

			const emptyStateContent =
				!isConversationAgentDefault && conversationAgent ? (
					<RovoAgentEmptyState
						agent={conversationAgent}
						addCurrentPageAsContextUserPreference={useCurrentPageContext}
					/>
				) : (
					<RovoEmptyState
						mode={mode || ConversationAssistantUIModes.SIDEBAR}
						conversationId={conversationId}
						sendMessage={emptyStateSendMessage}
						handleClickDynamicPrompt={handleClickDynamicPrompt}
						isResizeEnabled={isResizeEnabled}
						isResizeHeaderThresholdReached={isResizeHeaderThresholdReached}
						onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
					/>
				);

			if (shouldBypassTabsForEmptyLaunch) {
				return (
					<>
						<UFOCustomData
							data={{
								conversationHistorySubScreen: 'empty-state',
								conversationMessagesResponseCount,
							}}
						/>
						{emptyStateContent}
					</>
				);
			}

			if (
				(mode === ConversationAssistantUIModes.MINI_MODAL ||
					mode === ConversationAssistantUIModes.SIDEBAR) &&
				activeTabId !== undefined &&
				onActiveTabChange &&
				!selectedSpace &&
				!selectedPinnedItem &&
				isConversationAgentDefault &&
				elements?.shouldShowConversationActions !== false &&
				isFypTabsEnabled
			) {
				return (
					<>
						<UFOCustomData
							data={{
								conversationHistorySubScreen: 'empty-state-tabs',
								conversationMessagesResponseCount,
							}}
						/>
						<RovoTabs
							tabs={[
								{
									id: SUGGESTIONS_TAB_ID,
									label: formatMessage(tabMessages.quickActionsTabLabel),
									content: (
										<RovoEmptyState
											mode={mode || ConversationAssistantUIModes.SIDEBAR}
											conversationId={conversationId}
											sendMessage={emptyStateSendMessage}
											handleClickDynamicPrompt={handleClickDynamicPrompt}
											isResizeEnabled={isResizeEnabled}
											isResizeHeaderThresholdReached={isResizeHeaderThresholdReached}
											onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
											layout="quickActionsTab"
											onNestedViewChange={setIsInNestedActionView}
										/>
									),
								},
								{
									id: FOR_YOU_TAB_ID,
									label: formatMessage(tabMessages.yourWorkTabLabel),
									content: (
										<ForYouPanel
											cloudId={siteId}
											userId={profileData.aaid ?? ''}
											product={product}
											isActive={activeTabId === FOR_YOU_TAB_ID}
											onChatEmptyLaunch={handleChatEmptyLaunch}
										/>
									),
								},
							]}
							selectedTab={activeTabId}
							onTabChange={onActiveTabChange}
							hideTabStrip={isInNestedActionView}
						/>
					</>
				);
			}

			return (
				<>
					<UFOCustomData
						data={{
							conversationHistorySubScreen: 'empty-state',
							conversationMessagesResponseCount,
						}}
					/>
					{emptyStateContent}
				</>
			);
		},
		[
			conversationId,
			handleClickDynamicPrompt,
			conversationAgent,
			isConversationAgentDefault,
			mode,
			isResizeEnabled,
			isResizeHeaderThresholdReached,
			onDiscoverPromptLibraryClick,
			formatMessage,
			siteId,
			profileData,
			activeTabId,
			onActiveTabChange,
			runtimeCustomGreeting,
			handleChatEmptyLaunch,
			isFypTabsEnabled,
			selectedSpace,
			selectedPinnedItem,
			product,
			elements?.shouldShowConversationActions,
			conversationMessagesResponseCount,
			isInNestedActionView,
			useCurrentPageContext,
		],
	);

	const stabilizedRenderEmptyState = useMemo(
		() =>
			isRenderStabilizationEnabled ? renderEmptyStateContent(handleStabilizedSendMessage) : null,
		[isRenderStabilizationEnabled, renderEmptyStateContent, handleStabilizedSendMessage],
	);

	const legacyRenderEmptyState = useMemo(() => {
		if (isRenderStabilizationEnabled) {
			return null;
		}

		const handleSendMessage: EmptyStateSendMessage = (prompt, handleType, agent) => {
			sendEmptyStateMessage({
				agent,
				appFilters: currentChatMode.appFilters,
				conversationId,
				handleType,
				prompt,
				resolvedSpaceId:
					(selectedSpace?.id ?? conversation?.rovoSpaceId) &&
					FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
						? (selectedSpace?.id ?? conversation?.rovoSpaceId)
						: undefined,
				sendMessage,
				storeUserMessages,
				useCurrentPageContext: currentChatMode.useCurrentPageContext,
				webSearchEnabled: currentChatMode.webSearchEnabled,
			});
		};

		return renderEmptyStateContent(handleSendMessage);
	}, [
		isRenderStabilizationEnabled,
		renderEmptyStateContent,
		sendMessage,
		conversationId,
		storeUserMessages,
		currentChatMode,
		selectedSpace,
		conversation,
	]);

	return isRenderStabilizationEnabled ? stabilizedRenderEmptyState : legacyRenderEmptyState;
};
